import { APP_WEB_DOMAIN_LABELS } from '@/lib/constants/app';
import type { AppCfgsDto } from '@/lib/types/App';
import type { AppV1Browse, AppV1Item, AppV1Row } from '@/lib/types/AppV1';

/** Pure mapping logic for the in-dashboard TV preview (vitest-covered). */
export class AppPreviewUtils {
  /**
   * The app's public domain — the /app/v1 tenancy key. Custom domain wins,
   * matching the server's resolution order; null when the app has no web row
   * (no tenancy anchor) or an unknown shared domain.
   */
  static appDomain(app: AppCfgsDto): string | null {
    const web = app.web;
    if (!web) return null;
    if (web.core.customDomain) return web.core.customDomain;
    const label = APP_WEB_DOMAIN_LABELS[web.core.sharedDomainId];
    return label && web.core.customName ? `${web.core.customName}.${label}` : null;
  }

  /** Artwork URL for a row's aspect, with the contract's card fallback chain. */
  static artworkUrl(item: AppV1Item, itemAspect: string): string | null {
    const a = item.artwork;
    switch (itemAspect) {
      case '2x3':
        return a.poster2x3 ?? a.card ?? null;
      case '1x1':
        return a.square ?? a.card ?? null;
      case 'banner':
        return a.banner ?? a.card ?? null;
      default:
        return a.card ?? a.poster2x3 ?? null;
    }
  }

  /**
   * Rows worth rendering in a static preview: viewer-scoped rows (continue
   * watching) and off-air live rails arrive empty without a viewer session —
   * drop anything with no items rather than paint empty shelves.
   */
  static visibleRows(browse: AppV1Browse): AppV1Row[] {
    return browse.rows.filter((row) => row.items.length > 0);
  }

  /** CSS aspect-ratio value for a row's item aspect. */
  static aspectRatioCss(itemAspect: string): string {
    switch (itemAspect) {
      case '2x3':
        return '2 / 3';
      case '1x1':
        return '1 / 1';
      case 'banner':
        return '4 / 1';
      default:
        return '16 / 9';
    }
  }

  /** Card width (px at the preview's base scale) per item aspect. */
  static cardWidthPx(itemAspect: string): number {
    switch (itemAspect) {
      case '2x3':
        return 116;
      case '1x1':
        return 132;
      case 'banner':
        return 320;
      default:
        return 200;
    }
  }
}
